import { useTraceDataStore } from '@/stores/traceDataStore'
import GraphState from '../rappid/graph-state'
import { useHelpers } from './helpers'
import { EventBus } from '@/event-bus'
import { useBaseStore } from '@/stores'
import { darkenFillColor } from '@/pages/explorer/rappid/rappid-helpers'
import { injectTyped } from '@/inject'
import { ServiceKeys } from '@/services/symbols'

export function useCellSelection() {
  const rappidContext = injectTyped(ServiceKeys.rappidContext)

  const traceDataStore = useTraceDataStore()
  const baseStore = useBaseStore()

  const {
    getArtifactFromElementModel,
    getArtifactFromElementView,
    getLinksFromElementModel,
    updatePropertiesWidget,
    updatePropertiesWidgetForArtifactType,
    getArtifactTypeFromCellView,
  } = useHelpers()

  function resetAllHighlighted(paper: joint.dia.Paper): void {
    const elements = paper.model.getElements()
    for (let i = 0, ii = elements.length; i < ii; i++) {
      const currentElement = elements[i]
      const originalColor =
        currentElement.attr('body/originalFill') || currentElement.attr('body/fill')
      currentElement.attr('body/fill', originalColor)
      currentElement.attr({
        body: {
          strokeWidth: 0,
          stroke: '#000000',
          fill: currentElement.attr('body/originalFill') || currentElement.attr('body/fill'),
        },
      })
      currentElement.set('isSelected', false)
    }

    const links = paper.model.getLinks()
    for (let j = 0, jj = links.length; j < jj; j++) {
      const currentLink = links[j]
      currentLink.set('z', 3)
      currentLink.attr({
        line: {
          stroke: 'black',
          strokeWidth: 2,
          targetMarker: {
            fill: 'black',
          },
        },
      })
    }
  }

  function performSelection(elementView: joint.dia.ElementView): void {
    const artifact = getArtifactFromElementView(elementView)
    const artifactType = getArtifactTypeFromCellView(elementView)

    if (artifact != undefined || (artifactType != undefined && !elementView.model.get('isPill'))) {
      highlightElement(elementView)
      elementView.model.set('isSelected', true)
    }

    if (elementView.model.get('isPill') && !baseStore.getLoadingState) {
      if (elementView.model.get('searchMode')) {
        loadAdditionalHiddenArtifactsAfterSearch(elementView)
      } else {
        loadAdditionalHiddenArtifacts(elementView)
      }
    }
  }

  function loadAdditionalHiddenArtifacts(elementView: joint.dia.ElementView): void {
    const artifactType = getArtifactTypeFromCellView(elementView)

    if (artifactType) {
      const currentMaxArtifactCountToShow = artifactType.maxArtifactCountToShow ?? 4
      const newMaxArtifactCountToShow = currentMaxArtifactCountToShow + 4
      traceDataStore.setMaxArtifactCountToShow(artifactType, newMaxArtifactCountToShow)

      EventBus.emit('loadArtifactsFromApiAfterPillPress', {
        artifactType: artifactType,
        cellView: elementView,
        searchMode: false,
      })
    }
  }

  function loadAdditionalHiddenArtifactsAfterSearch(elementView: joint.dia.ElementView): void {
    const artifactType = getArtifactTypeFromCellView(elementView)
    if (artifactType) {
      const currentMaxArtifactCountToShow = artifactType.maxArtifactCountToShow ?? 4
      const newMaxArtifactCountToShow = currentMaxArtifactCountToShow + 4
      traceDataStore.setMaxArtifactCountToShow(artifactType, newMaxArtifactCountToShow)
    }

    if (artifactType)
      EventBus.emit('loadArtifactsFromApiAfterPillPress', {
        artifactType: artifactType,
        cellView: elementView,
        searchMode: true,
      })
  }

  function highlightElement(elementView: joint.dia.ElementView): void {
    if (traceDataStore.highlightAllDirectLinksOnSelection) {
      highlightDirectLinks(elementView)
    } else {
      highlightCell(elementView)
    }

    const artifact = getArtifactFromElementView(elementView)
    const artifactType = getArtifactTypeFromCellView(elementView)

    if (artifact !== undefined) {
      updatePropertiesWidget(artifact)
    }

    if (artifactType !== undefined) {
      updatePropertiesWidgetForArtifactType(artifactType)
    }
  }

  function highlightDirectLinks(cellView: joint.dia.ElementView): void {
    resetAllHighlighted(rappidContext.paper)
    const cell = (cellView as any).model
    rappidContext.paper.requireView(cell)

    if (cell.prop(GraphState.PROP_ARTIFACT)) {
      const links = rappidContext.graph.getConnectedLinks(cell)
      const neighbours = rappidContext.graph.getNeighbors(cell)
      const currentArtifact = getArtifactFromElementView(cellView)

      if (currentArtifact !== undefined) {
        currentArtifact.isHighlighted = true

        cell.attr({
          body: {
            strokeWidth: 3,
            stroke: '#000000',
          },
        })
      }

      links.forEach((element: any) => {
        const currentLink = getLinksFromElementModel(element)

        if (currentLink !== undefined) {
          currentLink.isHighlighted = true
          element.set('z', 100)
          element.attr({
            line: {
              stroke: '#1976d2',
              strokeWidth: 4,
              strokeLinejoin: 'round',
              targetMarker: {
                fill: '#1976d2',
              },
            },
          })
        }
      })

      neighbours.forEach((element: any) => {
        const currentNeighbourArtifact = getArtifactFromElementModel(element)

        if (currentNeighbourArtifact !== undefined) {
          currentNeighbourArtifact.isHighlighted = true
          element.attr({
            body: {
              strokeWidth: 3,
              stroke: '#000000',
            },
          })
        }
      })
    }
  }

  function highlightCell(cellView: joint.dia.CellView): void {
    resetAllHighlighted(rappidContext.paper)
    const cell = cellView.model
    rappidContext.paper.requireView(cell)
    if (cell.prop(GraphState.PROP_ARTIFACT) || cell.prop(GraphState.PROP_ARTIFACT_TYPE)) {
      cell.attr({
        body: {
          strokeWidth: -3,
          stroke: 'rgba(0, 0, 0, 0.5)',
        },
      })
      const currentFill = cellView.model.attr('body/fill')
      if (!cellView.model.attr('body/originalFill')) {
        cellView.model.attr('body/originalFill', currentFill)
      }
      cellView.model.attr('body/fill', darkenFillColor(currentFill))
    }
  }

  function highlightLink(linkView: joint.dia.LinkView): void {
    resetAllHighlighted(rappidContext.paper)
    linkView.model.set('z', 100)
    linkView.model.attr({
      line: {
        stroke: '#1976d2',
        strokeWidth: 4,
        strokeLinejoin: 'round',
        targetMarker: {
          fill: '#1976d2',
        },
      },
    })

    const currentLink = (linkView as any).model
    const neighbours = rappidContext.graph.getNeighbors(currentLink)
    neighbours.forEach((element: any) => {
      element.attr({
        body: {
          strokeWidth: 3,
          stroke: '#000000',
        },
      })
    })
  }

  return {
    /**
     * Remove highlighted styles for all elements and links.
     */
    resetAllHighlighted,
    /**
     * Highlight a single link and its two adjacent neighbour cells
     */
    highlightLink,
    /**
     * Highlight all links from and to the given element.
     */
    highlightDirectLinks,
    /**
     * Highlight a single link.
     */
    highlightCell,
    /**
     * Depending on store state, highlight either just the clicked element or all direct links.
     */
    highlightElement,
    /**
     * load the next set of hidden artifacts if there are any hidden artifacts.
     */
    loadAdditionalHiddenArtifacts,
    /**
     * Depending on the cell either performs highlighting or loads additional artifacts.
     */
    performSelection,
  }
}
